<template>
    <div class="pop_overlay_black">
        <div class="pop_wrap worker_stats" style="width: 900px; height: 690px">
            <div class="pop_title">
                <span class="title">{{ $t('work_schedule.rightpannel.label_staff_work_status') }}</span>
                <button class="mgl_auto" @click="action_Close">
                    <v-icon large>mdi-close</v-icon>
                </button>
            </div>
            <div class="pop_cont">
                <DateFilter :dateString="dateString" :action_BeforeMonth="action_BeforeMonth" :action_NextMonth="action_NextMonth" />
                <div class="table_wrap">
                    <div class="table2_xyscroll sticky" style="width: 100%; height: 100%" v-if="!isLoading">
                        <table class="table2_default">
                            <colgroup>
                                <col v-for="defaultColumn in defaultColumns" :key="defaultColumn.key" width="100" />
                                <col v-for="workCode in workCodes" :key="workCode._id" width="100" />
                            </colgroup>
                            <tr>
                                <th v-for="column in defaultColumns" :class="column.key === 'name' ? 'sticky name' : 'sticky'" :key="column.key" style="z-index: 8">
                                    {{ column.title }}
                                </th>
                                <th v-for="workCode in workCodes" :key="workCode._id">
                                    <span
                                        class="color_circle"
                                        :style="{ backgroundColor: workCode.customColorCode ?? basicPalette[workCode.paletteIndex] }"
                                        v-if="workCode._id !== null"
                                    ></span>
                                    <span class="ellipsis">{{ workCode.title }}</span>
                                </th>
                            </tr>
                            <tbody>
                                <template v-for="(department, deptIndex) in tableData">
                                    <tr v-for="(employee, empIndex) in department.employees" :key="`dept-${deptIndex}-emp-${empIndex}`">
                                        <th v-if="empIndex === 0" :rowspan="department.employees.length" class="sticky" style="top: inherit;">
                                            <span class="ellipsis">{{ department.title }}</span>
                                        </th>
                                        <td class="sticky name">
                                            <span class="ellipsis">{{ employee.name }}</span>
                                        </td>
                                        <td v-for="(score, scoreIndex) in employee.count" :key="`dept-${deptIndex}-emp-${empIndex}-score-${scoreIndex}`">
                                            {{ score }}
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { workerState } from '../../../worker-state'
import { sheetState } from '../../../../../../sheet-state'
import DateFilter from './DateFilter/DateFilter.vue'
import moment from '@/util/MPMoment'
import { useQueryClient } from '@tanstack/vue-query'
import { isApiErrorWithCode } from '@/apis/instances/check-api-response'
import { v2DepartmentFindQueryOptions } from '@/composables/queries/v2department/use-v2-department-find-query'
import { v2EmployeeFindQueryOptions } from '@/composables/queries/v2employee/use-v2-employee-find-query'
import { v2WorkCodeFindQueryOptions } from '@/composables/queries/v2workcode/use-v2-workcode-find-query'
import { statsV2WorkRecordDailyFindQueryOptions } from '@/composables/queries/stats/use-stats-v2workrecord-daily-find-query'

export default {
    setup() {
        // 팝업 이벤트 기반 명령형(await) 조회라 useQuery 대신 queryClient.fetchQuery 를 쓴다
        const queryClient = useQueryClient()
        return { queryClient }
    },
    components: {
        DateFilter
    },
    data() {
        return {
            worker: workerState,
            sheetState,
            defaultColumns: [
                { title: this.$t('work_schedule.popup_worker_stats.label_department'), key: 'department' },
                { title: this.$t('work_schedule.popup_worker_stats.label_name'), key: 'name' }
            ],
            selectedDate: moment(),

            workCodes: [],
            departments: [],
            employees: [],

            isLoading: true
        }
    },
    props: {
        isShow: Boolean
    },
    watch: {
        'worker.selectedDate': {
            handler: function (newValue, oldValue) {
                if (newValue == oldValue) { return }
                if (newValue?.format() == oldValue?.format()) { return }
                this.selectedDate = newValue
            },
            immediate: true
        },
        selectedDate: {
            handler: function () {
                this.loadAllData()
            },
            immediate: true
        }
    },
    computed: {
        basicPalette: function () {
            return this.sheetState.reservationSetting.workCodeColorPaletteID.basicPalette
        },
        dateString: function () {
            return moment(this.selectedDate).format(this.$t('work_schedule.date_format'))
        },
        startDate: function () {
            return moment(this.selectedDate).startOf('month').toString()
        },
        endDate: function () {
            return moment(this.selectedDate).endOf('month').toString()
        },
        hospitalID: function () {
            return this.hospital.selectedHospital._id
        },
        tableData: function () {
            return this.processDepartmentData(this.departments, this.employees, this.statsWorkRecord, this.workCodes)
        }
    },

    methods: {
        updateSelectedDate(months) {
            const date = moment(this.selectedDate).add(months, 'month').startOf('month')
            this.selectedDate = date
        },

        processDepartmentData(departments, employees, statsRecord, workCodes) {
            // '직접설정' 항목을 workCodes 배열의 끝에 추가
            const customSettingTitle = '직접설정'
            const customSettingExists = workCodes.some((workCode) => workCode.title === customSettingTitle)

            if (!customSettingExists) {
                workCodes.push({ _id: null, title: customSettingTitle })
            }

            const result = departments.map((department) => {
                const departmentEmployees = department.employees.map((employeeId) => {
                    const employee = employees.find((e) => e._id === employeeId)
                    const employeeStats = statsRecord.filter((stat) => stat.employeeID === employeeId)

                    // 모든 workCodes에 대한 count를 계산
                    const counts = workCodes.map((workCode) => {
                        if (workCode._id === null) {
                            // workCodeID가 null인 stats의 count를 모두 합산
                            return employeeStats.filter((stat) => stat.workCodeID === undefined).reduce((sum, record) => sum + record.count, 0)
                        } else {
                            // 특정 workCodeID에 대한 stats의 count를 찾는다
                            const stat = employeeStats.find((s) => s.workCodeID === workCode._id)
                            return stat ? stat.count : 0
                        }
                    })

                    return { name: employee.title ?? '', count: counts }
                })

                return { title: department.title, employees: departmentEmployees }
            })

            return result
        },
        loadAllData() {
            Promise.all([this.loadStatsWorkRecord(), this.loadWorkCodes(), this.loadDepartments()])
                .then(() => this.loadEmployees())
                .finally(() => {
                    this.isLoading = false
                })
        },

        // MARK: - Actions
        action_BeforeMonth() {
            this.updateSelectedDate(-1)
        },
        action_NextMonth() {
            this.updateSelectedDate(1)
        },
        action_Close() {
            this.$emit('hideWorkerStatsPopup')
        },

        // MARK: - Network
        async loadStatsWorkRecord() {
            const model = {
                hospitalID: this.hospitalID,
                startDate: this.startDate,
                endDate: this.endDate
            }
            try {
                this.statsWorkRecord = await this.queryClient.fetchQuery(statsV2WorkRecordDailyFindQueryOptions(model))
            } catch (err) {
                // 기존 동작 미러링: 봉투 code != 0 이면 갱신만 건너뛴다
                if (!isApiErrorWithCode(err)) throw err
            }
        },
        async loadWorkCodes() {
            const model = {
                hospitalID: this.hospitalID,
                removedAt: {
                    $gte: this.startDate,
                    $lte: this.endDate
                }
            }
            try {
                const workCodes = await this.queryClient.fetchQuery(v2WorkCodeFindQueryOptions(model))
                // 캐시 공유 배열을 직접 정렬하지 않도록 복사 후 정렬
                this.workCodes = [...workCodes].sort((l, r) => {
                    return l.sortNum > r.sortNum ? 1 : -1
                })
            } catch (err) {
                // 기존 동작 미러링: 봉투 code != 0 이면 갱신만 건너뛴다
                if (!isApiErrorWithCode(err)) throw err
            }
        },
        async loadDepartments() {
            const model = {
                hospitalID: this.hospitalID,
                removedAt: {
                    $gte: this.startDate,
                    $lte: this.endDate
                },
                way: 1,
                key: 'sortNum'
            }

            try {
                const data = await this.queryClient.fetchQuery(v2DepartmentFindQueryOptions(model))
                this.departments = data ?? []
            } catch (err) {
                // 기존 동작 미러링: 봉투 code != 0 이면 조용히 중단
                if (isApiErrorWithCode(err)) return
                throw err
            }
        },
        async loadEmployees() {
            const hospitalID = this.hospital.selectedHospital._id
            const model = {
                hospitalID: hospitalID,
                removedAt: {
                    $gte: this.startDate,
                    $lte: this.endDate
                },
                way: 1,
                key: 'sortNum',
                departmentID: this.departments.map((department) => department._id)
            }

            try {
                const result = await this.queryClient.fetchQuery(v2EmployeeFindQueryOptions(model))
                this.employees = result ?? []
            } catch (err) {
                // 기존 동작 미러링: 봉투 code != 0 이면 목록 갱신만 건너뛴다
                if (!isApiErrorWithCode(err)) throw err
            }
        }
    }
}
</script>
